import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Star, Clock } from 'lucide-react'
import { useGame } from '../context/GameContext'
import { useGameSession, formatTime } from '../context/GameSessionContext'
import { playCorrectSound, playMismatchSound, unlockAudio, playGameStartSound, speakChinese } from '../utils/sounds'
import GameComplete from '../components/GameComplete'
import { getRandomWords, getWordsByCategories } from '../data/vocabulary'
import { Category } from '../data/types'
import { MasterGameStat } from '../context/GameSessionContext'
import { scoreForCorrect } from '../utils/scoring'

interface MatchCard {
  id: string
  wordId: number
  content: string
  type: 'chinese' | 'english'
  isMatched: boolean
  isSelected: boolean
}

export default function GameMatching() {
  const MATCHING_BUILD_ID = 'matching-build-20260408-0652'
  const { updateProgress } = useGame()
  const { session, addScore, completeMasterGame } = useGameSession()
  const navigate = useNavigate()
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([])
  const [cards, setCards] = useState<MatchCard[]>([])
  const [selectedCards, setSelectedCards] = useState<MatchCard[]>([])
  const [matches, setMatches] = useState(0)
  const [round, setRound] = useState(1)
  const [isInitialized, setIsInitialized] = useState(false)
  const [gameComplete, setGameComplete] = useState(false)
  const [totalMatches, setTotalMatches] = useState(0)
  const [totalAttempts, setTotalAttempts] = useState(0)
  const [streak, setStreak] = useState(0)
  const [maxStreak, setMaxStreak] = useState(0)
  const isGeneratingRef = useRef(false)
  const usedWordIds = useRef<number[]>([])
  const correctWordIds = useRef<Set<number>>(new Set())
  const totalAttemptsRef = useRef(0)
  const elapsedTimeRef = useRef(0)
  const totalScoreRef = useRef(0)
  const computedMasterTotalWords = useMemo(() => {
    if (!session.isMasterMode) return 0
    if (selectedCategories.length === 0) return 0
    return getWordsByCategories(selectedCategories).length
  }, [session.isMasterMode, selectedCategories])

  const effectiveMasterTotalWords = session.isMasterMode
    ? (session.masterTotalWords > 0 ? session.masterTotalWords : computedMasterTotalWords)
    : 0
  const totalRounds = session.isMasterMode ? Math.max(1, Math.ceil(effectiveMasterTotalWords / 6)) : 3

  // Keep refs in sync immediately (avoid stale 0 values inside timeouts)
  totalAttemptsRef.current = totalAttempts
  elapsedTimeRef.current = session.elapsedTime
  totalScoreRef.current = session.totalScore

  useEffect(() => {
    totalAttemptsRef.current = totalAttempts
  }, [totalAttempts])

  useEffect(() => {
    elapsedTimeRef.current = session.elapsedTime
    totalScoreRef.current = session.totalScore
  }, [session.elapsedTime, session.totalScore, effectiveMasterTotalWords])

  // Load categories once on mount
  useEffect(() => {
    console.debug('[matching] build', MATCHING_BUILD_ID)
    unlockAudio()
    playGameStartSound()
    const stored = sessionStorage.getItem('selectedCategories')
    if (stored) {
      const parsed = JSON.parse(stored)
      setSelectedCategories(parsed)
      usedWordIds.current = []
      correctWordIds.current = new Set()
      setGameComplete(false)
      setIsInitialized(true)
    } else {
      navigate('/')
    }
  }, [navigate])

  // Check if session is active
  useEffect(() => {
    if (!session.isPlaying) {
      navigate('/')
    }
  }, [navigate, session.isPlaying])

  const generateCards = useCallback(() => {
    if (isGeneratingRef.current || selectedCategories.length === 0) return
    isGeneratingRef.current = true
    
    // In Master Mode, check if all words are already correct before generating
    const requiredWords = session.isMasterMode ? effectiveMasterTotalWords : totalRounds * 6
    if (session.isMasterMode && requiredWords > 0 && correctWordIds.current.size >= requiredWords) {
      console.debug('[matching] setGameComplete: allWordsAlreadyCorrect', {
        correct: correctWordIds.current.size,
        requiredWords,
      })
      setGameComplete(true)
      const stat: MasterGameStat = { correct: correctWordIds.current.size, total: totalAttemptsRef.current, time: elapsedTimeRef.current, score: totalScoreRef.current, maxStreak }
      completeMasterGame('matching', stat)
      setTimeout(() => { isGeneratingRef.current = false }, 100)
      return
    }
    
    // In Master Mode, exclude only correctWordIds. Excluding usedWordIds can exhaust the pool
    // and incorrectly end the game early.
    const excludeIds = session.isMasterMode
      ? [...correctWordIds.current]
      : usedWordIds.current
    
    // Try to get words from remaining pool (strict mode for Master Mode)
    const words = getRandomWords(6, excludeIds, selectedCategories, session.isMasterMode)
    
    // If no words available in Master Mode, game is complete
    if (session.isMasterMode && words.length === 0) {
      console.debug('[matching] setGameComplete: noWordsAvailable', {
        excludeCount: excludeIds.length,
        correct: correctWordIds.current.size,
        requiredWords,
      })
      setGameComplete(true)
      const stat: MasterGameStat = { correct: correctWordIds.current.size, total: totalAttempts, time: session.elapsedTime, score: session.totalScore, maxStreak: 0 }
      completeMasterGame('matching', stat)
      setTimeout(() => { isGeneratingRef.current = false }, 100)
      return
    }
    
    if (!session.isMasterMode) {
      words.forEach(w => usedWordIds.current.push(w.id))
    }
    const newCards: MatchCard[] = []
    
    words.forEach((word) => {
      newCards.push({
        id: `${word.id}-chinese`,
        wordId: word.id,
        content: word.chinese,
        type: 'chinese',
        isMatched: false,
        isSelected: false,
      })
      newCards.push({
        id: `${word.id}-english`,
        wordId: word.id,
        content: word.english,
        type: 'english',
        isMatched: false,
        isSelected: false,
      })
    })
    
    setCards(newCards.sort(() => Math.random() - 0.5))
    setSelectedCards([])
    setMatches(0)
    setStreak(0)
    
    setTimeout(() => {
      isGeneratingRef.current = false
    }, 100)
  }, [selectedCategories, session.isMasterMode, effectiveMasterTotalWords, totalRounds, completeMasterGame, maxStreak, totalAttempts, session.elapsedTime, session.totalScore])

  const generateCardsRef = useRef(generateCards)
  useEffect(() => {
    generateCardsRef.current = generateCards
  }, [generateCards])

  // Generate next round when `round` changes (more reliable than depending on cards.length===0)
  useEffect(() => {
    if (!isInitialized) return
    if (selectedCategories.length === 0) return
    if (gameComplete) return
    if (round === 1 && cards.length > 0) return

    isGeneratingRef.current = false
    generateCardsRef.current()
  }, [round, isInitialized, selectedCategories.length, gameComplete, cards.length])

  useEffect(() => {
    if (selectedCards.length === 2) {
      const [first, second] = selectedCards

      setTotalAttempts(prev => prev + 1)
      if (first.wordId === second.wordId && first.type !== second.type) {
        // Match!
        playCorrectSound()
        // Speak the Chinese word after the success chime so learners hear pronunciation
        const chineseContent = first.type === 'chinese' ? first.content : second.content
        setTimeout(() => speakChinese(chineseContent), 400)
        setTimeout(() => {
          setCards(prev => prev.map(card =>
            card.wordId === first.wordId ? { ...card, isMatched: true } : card
          ))
          // Balance scoring with other modes (perfect run ~1000 before multiplier)
          addScore(scoreForCorrect(streak), true)
          setMatches(prev => prev + 1)
          setTotalMatches(prev => prev + 1)
          updateProgress(first.wordId, true)
          // Increment streak
          setStreak(prev => {
            const newStreak = prev + 1
            setMaxStreak(current => Math.max(current, newStreak))
            return newStreak
          })
          // Track unique correct words for Master Mode
          if (session.isMasterMode) {
            correctWordIds.current.add(first.wordId)
          }
        }, 350)
      } else {
        // No match
        playMismatchSound()
        setTimeout(() => {
          setCards(prev => prev.map(card => ({ ...card, isSelected: false })))
        }, 600)
        addScore(0, false)
        // Reset streak on mismatch
        setStreak(0)
      }

      setSelectedCards([])
    }
  }, [selectedCards, updateProgress, addScore, session.isMasterMode])

  const handleCardClick = (card: MatchCard) => {
    if (card.isMatched || card.isSelected || selectedCards.length >= 2) return
    
    setCards(prev => prev.map(c => 
      c.id === card.id ? { ...c, isSelected: true } : c
    ))
    setSelectedCards(prev => [...prev, card])
  }

  const pairCount = Math.floor(cards.length / 2)

  // Check if all pairs are matched (more reliable than checking every card's isMatched)
  const allMatched = pairCount > 0 && matches >= pairCount

  // Auto-proceed when all matched
  useEffect(() => {
    if (!allMatched) return
    const timer = setTimeout(() => {
      // Master Mode: check if all unique words are correct
      const uniqueCorrectCount = correctWordIds.current.size
      const requiredWords = session.isMasterMode ? effectiveMasterTotalWords : totalRounds * 6

      console.debug('[matching] roundComplete', {
        round,
        totalRounds,
        uniqueCorrectCount,
        requiredWords,
        matches,
        pairCount,
        isMasterMode: session.isMasterMode,
      })
      
      // Normal mode: complete strictly by round count (prevents early completion)
      if (!session.isMasterMode) {
        if (round < totalRounds) {
          setRound(prev => prev + 1)
          setGameComplete(false)
          isGeneratingRef.current = false
          setSelectedCards([])
          setMatches(0)
          setCards([])
        } else {
          console.debug('[matching] setGameComplete: normalModeComplete')
          setGameComplete(true)
        }
        return
      }

      // Master Mode: complete only when all required words are correct
      if (requiredWords > 0 && uniqueCorrectCount >= requiredWords) {
        console.debug('[matching] setGameComplete: roundCompleteFinishedAllWords', {
          correct: uniqueCorrectCount,
          requiredWords,
        })
        const stat: MasterGameStat = { correct: uniqueCorrectCount, total: totalAttemptsRef.current, time: elapsedTimeRef.current, score: totalScoreRef.current, maxStreak }
        completeMasterGame('matching', stat)
        setGameComplete(true)
      } else {
        setRound(prev => prev + 1)
        setGameComplete(false)
        isGeneratingRef.current = false
        setSelectedCards([])
        setMatches(0)
        setCards([])
      }
    }, 1000)
    return () => clearTimeout(timer)
  }, [allMatched, round, totalRounds, session.isMasterMode, totalMatches, matches, completeMasterGame, effectiveMasterTotalWords, pairCount, maxStreak])

  return (
    <div className="page-shell bg-gradient-to-br from-green-500 via-emerald-500 to-teal-500 p-3 md:p-4 flex flex-col relative">
      <div className="max-w-6xl mx-auto w-full flex-1 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-3 flex-shrink-0 flex-wrap gap-2">
          <Link 
            to="/" 
            className="bg-white/20 rounded-2xl p-2 md:p-3 active:scale-95 transition-transform"
          >
            <ArrowLeft className="w-6 h-6 md:w-8 md:h-8 text-white" />
          </Link>
          
          <div className="flex items-center gap-2 md:gap-4 flex-wrap">
            <div className="bg-white/20 rounded-2xl px-3 py-1.5 md:px-4 md:py-2 flex items-center gap-1.5">
              <Clock className="w-4 h-4 md:w-5 md:h-5 text-white" />
              <span className="font-bold text-base md:text-xl text-white">
                {formatTime(session.elapsedTime)}
              </span>
            </div>
            <div className="bg-white/20 rounded-2xl px-3 py-1.5 md:px-4 md:py-2">
              <span className="text-white font-bold text-base md:text-xl">Round {round}/{totalRounds}</span>
            </div>
            <div className="bg-white/20 rounded-2xl px-3 py-1.5 md:px-4 md:py-2">
              <span className="text-white font-bold text-base md:text-xl">Matches: {matches}/{pairCount}</span>
            </div>
            <div className="bg-orange-400 rounded-2xl px-3 py-1.5 md:px-4 md:py-2">
              <span className="text-orange-900 font-bold text-base md:text-xl">🔥 {streak}</span>
            </div>
            <div className="bg-yellow-400 rounded-2xl px-3 py-1.5 md:px-4 md:py-2 flex items-center gap-1.5">
              <Star className="w-5 h-5 md:w-6 md:h-6 text-yellow-800" />
              <span className="text-yellow-900 font-bold text-base md:text-xl">{session.totalScore}</span>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-3 flex-shrink-0">
          <h2 className="text-lg md:text-2xl font-bold text-white">Match Chinese with English</h2>
        </div>

        {/* Cards Grid - 4x3 for landscape, 3x4 for portrait */}
        <div className="flex-1 grid grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3">
          {cards.map((card) => (
            <button
              key={card.id}
              onClick={() => handleCardClick(card)}
              disabled={card.isMatched}
              className={`
                rounded-xl md:rounded-2xl p-2 md:p-3 shadow-xl transition-all duration-200 flex items-center justify-center min-w-0 overflow-hidden
                ${card.isMatched 
                  ? 'bg-green-300/50 opacity-50 animate-match-pop' 
                  : card.isSelected
                    ? 'bg-yellow-400 scale-105'
                    : 'bg-white active:scale-95'
                }
              `}
            >
              <div className={`
                ${card.type === 'chinese' ? 'chinese-text text-lg md:text-3xl' : 'text-xs md:text-xl'}
                font-bold ${card.isMatched ? 'text-gray-400' : 'text-gray-800'} text-center break-words leading-tight
              `}>
                {card.content}
              </div>
            </button>
          ))}
        </div>

        {/* Round Complete - Banner */}
        {allMatched && !gameComplete && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-yellow-400 rounded-2xl px-6 py-3 shadow-2xl animate-flip">
            <p className="text-yellow-900 text-lg md:text-xl font-bold text-center whitespace-nowrap">
              🎉 All Matched! Round {round}/{totalRounds}
            </p>
          </div>
        )}
      </div>

      {/* Game Complete */}
      {gameComplete && (
        <GameComplete
          correctCount={totalMatches}
          totalQuestions={totalAttempts}
          gameName="Matching"
          isMatching={true}
          totalRounds={totalRounds}
          totalAttempts={totalAttempts}
        />
      )}
    </div>
  )
}
